import { Api, ModelRow } from "@mcleod/core";
import { BackOfficeReportPreview } from "@mcleod/settlement/src/BackOfficeReportPreview";
import { Button, Layout, ReportData, ReportPreviewTabSource, SlideoutDecorator, StepEvent } from "@mcleod/components";
import { AutogenLayoutManualOpenItemProcessingStepper } from "./autogen/AutogenLayoutManualOpenItemProcessingStepper";

export enum ManualOpenItemProcessingAction {
    PRINT_AND_POST,
    PRINT_ONLY,
    POST_ONLY
}

export enum ManualOpenItemProcessingStep {
    EDIT_LIST_STEP = 0,
    PRE_POST_REVIEW_STEP = 1,
    POSTING_REPORTS_STEP = 2
}

export interface ManualOpenItemProcessingStepperProps {
    action: ManualOpenItemProcessingAction;
    selectedRows: ModelRow[];
    doAfterSlideOut?: (postingCompleted: boolean, postedCount: number) => void;
}

export class ManualOpenItemProcessingStepper extends AutogenLayoutManualOpenItemProcessingStepper {
    private minAllowedStepIndex: number = 0;
    private maxAllowedStepIndex: number = 0;
    private reportPreviewEditList: BackOfficeReportPreview;
    private reportPreviewPostingReports: BackOfficeReportPreview;
    private buttonContinue: Button;
    private globalProps: ManualOpenItemProcessingStepperProps;

    // postingAttempted is used in doAfterSlideOut to indicate that the posting process was not cancelled before posting.
    // It will be true even if no open items were posted due to exceptions.
    private postingAttempted: boolean = false;
    private postedCount: number = 0;

    static showAsSlideout(props: ManualOpenItemProcessingStepperProps) {
        const stepperLayout = Layout.getLayout("lme/ar/ManualOpenItemProcessingStepper") as ManualOpenItemProcessingStepper;

        SlideoutDecorator.decorateAndSlideIn(stepperLayout, {
            title: `Print/Post (${props.selectedRows.length}) Records`,
            fillVerticalSpace: true,
            width: "60%",
            addlComponents: [ stepperLayout.createContinueButton(props) ],
            overlayProps: { greyedBackground: true },
            layoutLoadListeners: () => {
                stepperLayout.globalProps = props;
                stepperLayout.initializeEditListPanel();
                stepperLayout.initializePostingReportsPanel();
            },
            doAfterSlideIn: async () => {
                switch (props.action) {
                    case ManualOpenItemProcessingAction.PRINT_AND_POST:
                    case ManualOpenItemProcessingAction.PRINT_ONLY:
                        // Both PRINT_AND_POST and PRINT_ONLY actions require printing the edit list first, but subsequent steps differ
                        stepperLayout.printEditList(props);
                        break;
                    case ManualOpenItemProcessingAction.POST_ONLY:
                        stepperLayout.minAllowedStepIndex = ManualOpenItemProcessingStep.PRE_POST_REVIEW_STEP;
                        stepperLayout.updateStep(ManualOpenItemProcessingStep.PRE_POST_REVIEW_STEP);
                        await stepperLayout.layoutManualOpenItemProcessingPrePostReview.populateTotalsForReview(props);
                        break;
                }
            },
            doAfterSlideOut: () => {
                if (props.doAfterSlideOut) {
                    props.doAfterSlideOut(stepperLayout.postingAttempted, stepperLayout.postedCount);
                }
            }
        });
    }

    createContinueButton(props?: ManualOpenItemProcessingStepperProps): Button {
        if (this.buttonContinue == null) {
            this.buttonContinue = new Button({
                id: "buttonContinue",
                caption: this.getInitialContinueButtonCaption(props),
                color: "primary.reverse",
                backgroundColor: "primary",
                width: 200,
                rowBreak: false,
                borderWidth: 0,
                onClick: () => {
                    this.advanceToNextStep();
                }
            });
        }

        return this.buttonContinue;
    }

    private getInitialContinueButtonCaption(props?: ManualOpenItemProcessingStepperProps): string {
        switch (props?.action) {
            case ManualOpenItemProcessingAction.PRINT_AND_POST:
                return "Confirm Manual Open Items";
            case ManualOpenItemProcessingAction.PRINT_ONLY:
                return "Close";
            case ManualOpenItemProcessingAction.POST_ONLY:
                return "Post";
            default:
                return "Continue";
        }
    }

    stepperManualOpenItemProcessingBeforeStepListener(event: StepEvent) {
        const stepIndex = event.newIndex;
        // Prevent advancing to next step without using the continue button first, and prevent going back beyond allowed steps.
        if (stepIndex > this.maxAllowedStepIndex || stepIndex < this.minAllowedStepIndex) {
            event.preventDefault();
        } else {
            this.updateContinueButtonCaption(stepIndex);
        }
    }

    async advanceToNextStep() {
        if (this.stepEditList.selected) {
            if (this.globalProps.action === ManualOpenItemProcessingAction.PRINT_AND_POST) {
                this.updateStep(ManualOpenItemProcessingStep.PRE_POST_REVIEW_STEP);
                this.buttonContinue.busy = true;
                await this.layoutManualOpenItemProcessingPrePostReview.populateTotalsForReview(this.globalProps);
                this.buttonContinue.busy = false;
                this.updateContinueButtonCaption(ManualOpenItemProcessingStep.PRE_POST_REVIEW_STEP);
            } else if (this.globalProps.action === ManualOpenItemProcessingAction.PRINT_ONLY) {
                this.slideOut();
            }
        } else if (this.stepPrePostReview.selected) {
            this.updateStep(ManualOpenItemProcessingStep.POSTING_REPORTS_STEP);
            this.buttonContinue.busy = true;
            await this.postManualOpenItems(this.globalProps);
            this.buttonContinue.busy = false;
            this.updateContinueButtonCaption(ManualOpenItemProcessingStep.POSTING_REPORTS_STEP);
        } else if (this.stepPostingReports.selected) {
            this.slideOut();
        }
    }

    updateStep(newIndex: number) {
        this.updateMaxAllowedIndex(newIndex);
        this.stepperManualOpenItemProcessing.selectedIndex = newIndex;
    }

    updateMaxAllowedIndex(newIndex: number) {
        if (newIndex > this.maxAllowedStepIndex) {
            this.maxAllowedStepIndex = newIndex;
        }
    }

    updateContinueButtonCaption(newIndex: number) {
        switch (newIndex) {
            case ManualOpenItemProcessingStep.EDIT_LIST_STEP:
                this.buttonContinue.caption = "Confirm Manual Open Items";
                break;
            case ManualOpenItemProcessingStep.PRE_POST_REVIEW_STEP:
                this.buttonContinue.caption = "Post";
                break;
            case ManualOpenItemProcessingStep.POSTING_REPORTS_STEP:
                this.buttonContinue.caption = "Close";
                break;
        }
    }

    // START: Edit List Step Logic
    private initializeEditListPanel() {
        this.reportPreviewEditList = new BackOfficeReportPreview("Edit List", true);
        this.stepEditList.add(this.reportPreviewEditList);
    }

    private async printEditList(props: ManualOpenItemProcessingStepperProps) {
        this.buttonContinue.busy = true;
        const response = await Api.post("lme/ar/print-manual-open-item-edit-list", { open_items: props.selectedRows });
        this.buttonContinue.busy = false;

        const data = response?.data[0];
        this.reportPreviewEditList.beginSearch();
        const tabSources: ReportPreviewTabSource[] = [
            ...data.all_report_data as ReportData[]
        ];
        this.reportPreviewEditList.endSearch(this.cleanTabSources(tabSources));
    }
    // END: Edit List Step logic

    // START: Posting Reports Step Logic
    private initializePostingReportsPanel() {
        this.reportPreviewPostingReports = new BackOfficeReportPreview("Posting Reports", true);
        this.stepPostingReports.add(this.reportPreviewPostingReports);
    }

    private async postManualOpenItems(props: ManualOpenItemProcessingStepperProps) {
        const response = await Api.post("lme/ar/post-manual-open-items", { open_items: props.selectedRows });
        const data = response?.data[0];

        this.reportPreviewPostingReports.beginSearch();
        const tabSources: ReportPreviewTabSource[] = [
            ...data.all_report_data as ReportData[]
        ];
        this.reportPreviewPostingReports.endSearch(this.cleanTabSources(tabSources));

        this.postingAttempted = true;
        this.postedCount = data.posted_count || 0;
    }
    // END: Posting Reports Step Logic

    cleanTabSources(tabSources: ReportPreviewTabSource[]): ReportPreviewTabSource[] {
        return tabSources.filter(data => data != null);
    }
}
